import React, { createContext, useContext, useMemo, useEffect, useRef } from 'react';
import { useClient, useConnectorClient, useChainId, useAccount } from 'wagmi';
import { BrowserProvider, JsonRpcSigner, FallbackProvider, JsonRpcProvider } from 'ethers';
import { getProviderCompat as getCompatProvider, getSignerCompat as getCompatSigner } from '../utils/ethersCompat';
import { getChainId, getAddressesById } from '../utils/address';

const EthersCtx = createContext({ provider: undefined, signer: undefined, chainId: undefined, addresses: undefined });

function clientToProvider(client) {
  if (!client) return undefined;
  const { chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const envUrl =
    (typeof process !== 'undefined' && process?.env?.REACT_APP_RPC_URL) ||
    (typeof process !== 'undefined' && process?.env?.VITE_RPC_URL) ||
    (typeof process !== 'undefined' && process?.env?.NEXT_PUBLIC_RPC_URL);
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return new JsonRpcProvider(envUrl.trim(), network);
  }
  if (transport?.type === 'fallback') {
    const providers = (transport.transports || []).map(({ value }) => new JsonRpcProvider(value?.url, network));
    if (providers.length === 1) return providers[0];
    return new FallbackProvider(providers);
  }
  return new JsonRpcProvider(transport?.url, network);
}

function clientToSigner(client) {
  if (!client) return undefined;
  const { account, chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new BrowserProvider(transport, network);
  const signer = new JsonRpcSigner(provider, account.address);
  return signer;
}

export function EthersProvider({ children }) {
  const chainIdHook = useChainId();
  const publicClient = useClient({ chainId: chainIdHook || getChainId() });
  const { data: walletClient } = useConnectorClient({ chainId: chainIdHook || getChainId() });
  const addresses = getAddressesById(chainIdHook || getChainId());

  const provider = useMemo(() => {
    const fromWagmi = clientToProvider(publicClient);
    return fromWagmi || getCompatProvider();
  }, [publicClient]);

  const signer = useMemo(() => {
    const fromWagmi = clientToSigner(walletClient);
    return fromWagmi || undefined;
  }, [walletClient]);

  const value = useMemo(() => ({ provider, signer, chainId: chainIdHook || getChainId(), addresses }), [provider, signer, chainIdHook, addresses]);
  return <EthersCtx.Provider value={value}>{children}</EthersCtx.Provider>;
}

export function useEthers() {
  return useContext(EthersCtx);
}

export function EthersAutoReload() {
  const chainId = useChainId();
  const { address } = useAccount();
  const prev = useRef({ initialized: false, chainId: undefined, address: undefined });
  const timer = useRef(null);
  useEffect(() => {
    const currAddr = address || undefined;
    const currChain = Number.isFinite(Number(chainId)) ? Number(chainId) : undefined;
    if (!prev.current.initialized) {
      prev.current = { initialized: true, chainId: currChain, address: currAddr };
      return;
    }
    const changed =
      ((prev.current.chainId !== undefined && currChain !== undefined && prev.current.chainId !== currChain) ||
       (prev.current.address !== undefined && currAddr !== undefined && prev.current.address !== currAddr));
    if (changed) {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (typeof window !== 'undefined') {
          const key = `${currChain || ''}:${currAddr || ''}`;
          const last = window.sessionStorage.getItem('autoReloadGuard');
          if (last !== key) {
            window.sessionStorage.setItem('autoReloadGuard', key);
            window.location.reload();
          }
        }
      }, 200);
    }
    prev.current = { initialized: true, chainId: currChain, address: currAddr };
  }, [chainId, address]);
  return null;
}
